import { checkIsLoop, checkIsLoopEnd } from "@/core/util";

// --- Configuration flags ---

/** Whether structural nodes (LOOP, LOOP_END, ROUTER) receive a stepNumber. */
export const STRUCTURAL_NODES_GET_NUMBER = true;

/**
 * How output dropdown labels display step numbers:
 * - "both"       → "#5 (3). Step - ..."  (stable #5, positional 3rd)
 * - "positional"  → "3. Step - ..."       (original behaviour)
 * - "stable"      → "5. Step - ..."       (unique number only)
 */
export const OUTPUT_DISPLAY_MODE: "both" | "positional" | "stable" = "stable";

// --- Core functions ---

/**
 * Returns true if this step type should be assigned a unique stepNumber.
 */
export function shouldAssignStepNumber(step: any): boolean {
  if (STRUCTURAL_NODES_GET_NUMBER) {
    return true;
  }
  // Structural nodes: LOOP, LOOP_END, ROUTER — skip them
  if (step?.type === "ROUTER") return false;
  if (checkIsLoop(step)) return false;
  if (checkIsLoopEnd(step)) return false;
  return true;
}

/**
 * Assigns a unique, monotonically increasing stepNumber to a step
 * if it is eligible and does not already have one.
 * Mutates step in-place and increments flow.nextStepNumber.
 */
export function assignStepNumber(step: any, flow: any): void {
  if (!step || !flow) return;
  if (!shouldAssignStepNumber(step)) return;
  if (step.stepNumber != null && step.stepNumber > 0) return; // already assigned

  if (flow.nextStepNumber == null) {
    flow.nextStepNumber = 1;
  }
  step.stepNumber = flow.nextStepNumber;
  flow.nextStepNumber++;
}

/**
 * Recursively walks all steps (including router condition steps)
 * and assigns stepNumbers to those that are eligible but missing one.
 * Also computes `flow.nextStepNumber` as max(existing) + 1.
 * Used for migrating existing flows that were saved without stepNumbers.
 */
export function migrateStepNumbers(steps: any[], flow: any): void {
  if (!steps || !flow) return;

  // First pass: find the maximum existing stepNumber
  let maxExisting = 0;
  walkSteps(steps, (step) => {
    if (step.stepNumber != null && step.stepNumber > maxExisting) {
      maxExisting = step.stepNumber;
    }
  });

  // Initialise counter above any existing number
  if (flow.nextStepNumber == null || flow.nextStepNumber <= maxExisting) {
    flow.nextStepNumber = maxExisting + 1;
  }

  // Second pass: assign numbers to eligible steps without one
  walkSteps(steps, (step) => {
    assignStepNumber(step, flow);
  });
}

/**
 * Restores stepNumber from backend's ix before useAddPositionToStep
 * overwrites ix with the positional index.
 * Also computes flow.nextStepNumber as max(ix) + 1.
 */
export function restoreStepNumbersFromIx(steps: any[], flow: any): void {
  if (!steps || !flow) return;

  let minIx = Number.POSITIVE_INFINITY;
  walkSteps(steps, (step) => {
    if (typeof step?.ix === "number") {
      minIx = Math.min(minIx, step.ix);
    }
  });

  // Some wizard-created flows can come back with 0-based ix values.
  // Shift to 1-based to keep UI numbering consistent.
  const offset = minIx === 0 ? 1 : 0;

  let maxStepNumber = 0;
  walkSteps(steps, (step) => {
    if (typeof step?.ix === "number") {
      const restored = step.ix + offset;
      if (restored > 0) {
        step.stepNumber = restored;
        if (restored > maxStepNumber) {
          maxStepNumber = restored;
        }
      } else {
        step.stepNumber = undefined;
      }
    }
  });

  flow.nextStepNumber = maxStepNumber + 1;
}

/**
 * Before sending to backend, swaps ix with stepNumber and stores
 * the positional ix in the order field.
 * Only modifies steps that have a stepNumber (i.e. eligible nodes).
 * Steps without stepNumber (structural nodes) keep their positional ix.
 */
export function overrideIxForBackend(steps: any[]): void {
  if (!steps) return;
  walkSteps(
    steps,
    (step, isRoute) => {
      step.order = step.ix; // preserve positional ordering
      if (!isRoute && step.stepNumber != null) {
        step.ix = step.stepNumber; // backend receives unique ix (steps only)
      }
    },
    true
  );
}

/**
 * Formats a step label for output dropdowns based on OUTPUT_DISPLAY_MODE.
 * @param positionalIndex - the ix
 * @param stepNumber - the unique stable number (might be undefined)
 * @returns display string like "#5 (3)" or "3" or "5"
 */
export function formatOutputStepLabel(
  positionalIndex: number,
  stepNumber?: number
): string {
  const positional = positionalIndex + 1;

  if (stepNumber == null || stepNumber <= 0) {
    return `${positional}`;
  }

  switch (OUTPUT_DISPLAY_MODE) {
    case "both":
      return `#${stepNumber} (${positional})`;
    case "stable":
      return `${stepNumber}`;
    case "positional":
    default:
      return `${positional}`;
  }
}

// --- Internal helper ---

/**
 * Recursively walks all steps, including router condition steps.
 * Calls `callback` for every step and condition.
 */
function walkSteps(
  steps: any[],
  callback: (step: any, isRoute: boolean) => void,
  visitRoutes = false
): void {
  if (!steps) return;
  // eslint-disable-next-line no-restricted-syntax
  for (const step of steps) {
    callback(step, false);
    if (step.type === "ROUTER" && step.routes) {
      // eslint-disable-next-line no-restricted-syntax
      for (const route of step.routes) {
        if (visitRoutes) callback(route, true);
        if (route.steps) {
          walkSteps(route.steps, callback, visitRoutes);
        }
      }
    }
  }
}
